import { useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/auth-context';
import { useOfficeType } from '@/hooks/use-office-type';
import { supabase } from '@/integrations/supabase/client';
import { featureGateAccessReader, requestFeatureGateAccess } from '@/lib/boot-requests';
import { hasUnexpiredSession } from '@/lib/session-expiry';
import {
  EMPTY_FEATURE_ACCESS,
  FEATURE_GATE_DEFINITIONS,
  isKnownFeatureGateKey,
  isFeatureGateReleaseStage,
} from '@/lib/feature-gates';
import { maskFeatureAccessForOfficeType } from '@/lib/office-type-features';
import { isQueryableOrganizationId } from '@/lib/organization-id';

import type { Session } from '@supabase/supabase-js';
import type { FeatureGateAccessReader } from '@/lib/boot-requests';
import type {
  FeatureGateAccess,
  FeatureGateDefinition,
  FeatureGateKey,
  FeatureGateReleaseStage,
} from '@/lib/feature-gates';

interface FeatureGateRow {
  feature_key: FeatureGateKey;
  release_stage: FeatureGateReleaseStage;
  updated_at: string | null;
}

interface FeatureGateOfficeRow {
  feature_key: FeatureGateKey;
  organization_id: string;
}

interface FeatureGateAccessRow {
  feature_key: FeatureGateKey;
  enabled: boolean;
}

export interface PlatformFeatureGateState extends FeatureGateDefinition {
  releaseStage: FeatureGateReleaseStage;
  selectedOfficeIds: readonly string[];
  updatedAt: string | null;
}

const FEATURE_GATE_QUERY_PREFIX = 'feature-gates';
const FEATURE_GATE_REFRESH_STORAGE_KEY = 'pulse_feature_gates_refresh';

export const FEATURE_GATE_QUERY_KEYS = {
  // sessionExpiresAt is a key dimension on purpose: the queryFn guard can write a
  // fail-closed EMPTY_FEATURE_ACCESS during the expired window, and on a silent token
  // refresh `enabled` only goes true→true under an unchanged org/user key — TanStack
  // never refetches on that transition, so without a changing key the gate stays
  // stuck-closed. A fresh expires_at moves the query to a new entry that refetches.
  access: (
    organizationId: string | null | undefined,
    userId: string | null | undefined,
    sessionExpiresAt: number | null | undefined,
  ) => [FEATURE_GATE_QUERY_PREFIX, 'access', organizationId, userId, sessionExpiresAt],
  platform: [FEATURE_GATE_QUERY_PREFIX, 'platform'],
} as const;

function notifyFeatureGateAccessChanged(): void {
  if (typeof globalThis.dispatchEvent !== 'function') {
    return;
  }

  const updatedAt = new Date().toISOString();
  globalThis.dispatchEvent(new globalThis.CustomEvent(FEATURE_GATE_REFRESH_STORAGE_KEY));
  try {
    globalThis.localStorage?.setItem(FEATURE_GATE_REFRESH_STORAGE_KEY, updatedAt);
  } catch {
    // Storage can be unavailable in hardened browser contexts; same-tab events still refresh.
  }
}

function toFeatureGateError(error: { message?: string }): Error {
  return new Error(error.message ?? 'Feature gate request failed');
}

function isFeatureGateRow(row: unknown): row is FeatureGateRow {
  if (typeof row !== 'object' || row === null) {
    return false;
  }
  const candidate = row as Partial<FeatureGateRow>;
  return (
    typeof candidate.feature_key === 'string' &&
    isKnownFeatureGateKey(candidate.feature_key) &&
    typeof candidate.release_stage === 'string' &&
    isFeatureGateReleaseStage(candidate.release_stage)
  );
}

function isFeatureGateOfficeRow(row: unknown): row is FeatureGateOfficeRow {
  if (typeof row !== 'object' || row === null) {
    return false;
  }
  const candidate = row as Partial<FeatureGateOfficeRow>;
  return (
    typeof candidate.feature_key === 'string' &&
    isKnownFeatureGateKey(candidate.feature_key) &&
    typeof candidate.organization_id === 'string'
  );
}

function isFeatureGateAccessRow(row: unknown): row is FeatureGateAccessRow {
  if (typeof row !== 'object' || row === null) {
    return false;
  }
  const candidate = row as Partial<FeatureGateAccessRow>;
  return (
    typeof candidate.feature_key === 'string' &&
    isKnownFeatureGateKey(candidate.feature_key) &&
    typeof candidate.enabled === 'boolean'
  );
}

function toRows<T>(data: unknown, isRow: (row: unknown) => row is T): T[] {
  return Array.isArray(data) ? data.filter(isRow) : [];
}

function withFeatureAccessEntry(
  access: FeatureGateAccess,
  key: FeatureGateKey,
  enabled: boolean,
): FeatureGateAccess {
  return { ...access, [key]: enabled };
}

export { hasUnexpiredSession };

/**
 * A gate answer with failures kept DISTINCT from a genuine all-off.
 *
 * `fetchFeatureGateAccess` deliberately flattens the two (fail-closed is the
 * right default for a UI gate: an unanswered flag hides the feature). A caller
 * that acts DESTRUCTIVELY on "off" — the offline cache's kill switch deletes
 * cached constituent data — cannot use that flattening, because one statement
 * timeout would then read as a deliberate shutdown. Such callers resolve here
 * and treat `unavailable` as "ask again later".
 */
export type FeatureGateAnswer =
  | { readonly status: 'answered'; readonly access: FeatureGateAccess }
  | { readonly status: 'unavailable' };

const GATE_UNAVAILABLE: FeatureGateAnswer = { status: 'unavailable' };

export async function resolveFeatureGateAccess(
  organizationId: string | null | undefined,
  session: Session | null,
  readAccess: FeatureGateAccessReader = requestFeatureGateAccess,
): Promise<FeatureGateAnswer> {
  // The platform-admin sentinel is not a uuid: sending it reaches Postgres as a 22P02
  // on p_organization_id, which surfaces as a failed gate rather than "no org selected".
  if (!isQueryableOrganizationId(organizationId)) {
    return GATE_UNAVAILABLE;
  }

  // Re-check at call time, not just in `enabled`: a focus/reconnect refetch (or a
  // wake-from-sleep) can run after the token expired but before a re-render flips
  // `enabled`. expires_at is absolute, so the same closure session reads as expired
  // once wall-clock passes it — catching the stale-render window the gate alone can't.
  if (!hasUnexpiredSession(session)) {
    return GATE_UNAVAILABLE;
  }

  const { data, error } = await readAccess(organizationId);
  if (error) {
    if (error.code === '42501') {
      console.warn('feature_gate_access denied (insufficient privilege)', { code: error.code });
    }
    // 42501 included: "the RPC would not answer me" is not the same statement
    // as "every gate is off", and membership is proven elsewhere.
    return GATE_UNAVAILABLE;
  }

  return {
    status: 'answered',
    access: toRows(data, isFeatureGateAccessRow).reduce(
      (access, row) => withFeatureAccessEntry(access, row.feature_key, row.enabled),
      EMPTY_FEATURE_ACCESS,
    ),
  };
}

export async function fetchFeatureGateAccess(
  organizationId: string | null | undefined,
  session: Session | null,
  readAccess?: FeatureGateAccessReader,
): Promise<FeatureGateAccess> {
  const answer = await resolveFeatureGateAccess(organizationId, session, readAccess);
  return answer.status === 'answered' ? answer.access : EMPTY_FEATURE_ACCESS;
}

export function useOrganizationFeatureAccess(organizationId: string | null | undefined) {
  const queryClient = useQueryClient();
  const { session } = useAuth();

  useEffect(() => {
    if (!organizationId || typeof globalThis.addEventListener !== 'function') {
      return;
    }

    const invalidateFeatureAccess = () => {
      queryClient
        .invalidateQueries({ queryKey: [FEATURE_GATE_QUERY_PREFIX, 'access'] })
        .catch(() => null);
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === FEATURE_GATE_REFRESH_STORAGE_KEY) {
        invalidateFeatureAccess();
      }
    };

    globalThis.addEventListener(FEATURE_GATE_REFRESH_STORAGE_KEY, invalidateFeatureAccess);
    globalThis.addEventListener('storage', handleStorage);

    return () => {
      globalThis.removeEventListener(FEATURE_GATE_REFRESH_STORAGE_KEY, invalidateFeatureAccess);
      globalThis.removeEventListener('storage', handleStorage);
    };
  }, [organizationId, queryClient]);

  // Key on session validity, not mere presence: feature-gate queries are persisted to
  // localStorage (see query-persistence.ts), so a successful entry can be rehydrated into a
  // render where the seeded session is already expired. enabled:false stops the refetch but
  // TanStack still serves cached data for a matching key — keying expired sessions under a
  // distinct (null-identity) entry keeps those stale enabled gates from surfacing.
  const sessionIsValid = hasUnexpiredSession(session);
  const officeType = useOfficeType(organizationId);
  const maskForOfficeType = useCallback(
    (access: FeatureGateAccess) => maskFeatureAccessForOfficeType(access, officeType),
    [officeType],
  );

  return useQuery({
    // Don't refetch an org-scoped RPC as `anon` after JWT expiry — see hasUnexpiredSession.
    enabled: isQueryableOrganizationId(organizationId) && sessionIsValid,
    queryKey: FEATURE_GATE_QUERY_KEYS.access(
      organizationId,
      sessionIsValid ? session?.user.id : undefined,
      sessionIsValid ? session?.expires_at : undefined,
    ),
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
    queryFn: async () =>
      await fetchFeatureGateAccess(
        organizationId,
        session,
        featureGateAccessReader(session?.user.id ?? ''),
      ),
    select: maskForOfficeType,
  });
}

export function usePlatformFeatureGates() {
  return useQuery({
    queryKey: FEATURE_GATE_QUERY_KEYS.platform,
    // Persisted catalogues can predate the registry bundled with this release.
    refetchOnMount: 'always',
    staleTime: 30_000,
    queryFn: async (): Promise<PlatformFeatureGateState[]> => {
      const [featureResult, officeResult] = await Promise.all([
        supabase
          .from('feature_gates')
          .select('feature_key,release_stage,updated_at')
          .order('feature_key', { ascending: true }),
        supabase
          .from('feature_gate_offices')
          .select('feature_key,organization_id')
          .order('organization_id', { ascending: true }),
      ]);

      if (featureResult.error) {
        throw toFeatureGateError(featureResult.error);
      }
      if (officeResult.error) {
        throw toFeatureGateError(officeResult.error);
      }

      const featureRows = toRows(featureResult.data, isFeatureGateRow);
      const officeRows = toRows(officeResult.data, isFeatureGateOfficeRow);
      const featureRowsByKey = new Map(featureRows.map((row) => [row.feature_key, row]));

      return FEATURE_GATE_DEFINITIONS.map((feature) => {
        const row = featureRowsByKey.get(feature.key);
        return {
          ...feature,
          releaseStage: row?.release_stage ?? 'in_development',
          selectedOfficeIds: officeRows
            .filter((officeRow) => officeRow.feature_key === feature.key)
            .map((officeRow) => officeRow.organization_id),
          updatedAt: row?.updated_at ?? null,
        };
      });
    },
  });
}

export function useUpdateFeatureGateStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      featureKey,
      releaseStage,
    }: {
      featureKey: FeatureGateKey;
      releaseStage: FeatureGateReleaseStage;
    }) => {
      const { error } = await supabase.rpc('set_feature_gate_release_stage', {
        p_feature_key: featureKey,
        p_release_stage: releaseStage,
      });

      if (error) {
        throw toFeatureGateError(error);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: FEATURE_GATE_QUERY_KEYS.platform });
      await queryClient.invalidateQueries({ queryKey: [FEATURE_GATE_QUERY_PREFIX, 'access'] });
      notifyFeatureGateAccessChanged();
    },
  });
}

export function useSetFeatureGateTrialOffice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      featureKey,
      organizationId,
      enabled,
    }: {
      featureKey: FeatureGateKey;
      organizationId: string;
      enabled: boolean;
    }) => {
      const { error } = await supabase.rpc('set_feature_gate_trial_office', {
        p_enabled: enabled,
        p_feature_key: featureKey,
        p_organization_id: organizationId,
      });

      if (error) {
        throw toFeatureGateError(error);
      }
    },
    // The server refuses an add once a gate stops trialling; show the stage that caused it.
    onError: () => {
      queryClient
        .invalidateQueries({ queryKey: FEATURE_GATE_QUERY_KEYS.platform })
        .catch(() => null);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: FEATURE_GATE_QUERY_KEYS.platform });
      await queryClient.invalidateQueries({ queryKey: [FEATURE_GATE_QUERY_PREFIX, 'access'] });
      notifyFeatureGateAccessChanged();
    },
  });
}
